import { Grid2 as Grid, Stack, Typography } from '@mui/material';

import {
  EditableListDataGridLayout,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
  useListContext,
} from 'framework/list';
import { EnumDropdown, FormFieldsData, NumberInput, TextInput } from 'framework/form';
import { createTruncatedTextFormat, FormattedTextField } from 'framework/ui/field';
import {
  FTEBadge,
  ProjectTeamIssuesField,
  ProjectTeamMemberPositionField,
  ProjectTeamMemberRoleField,
  ProjectTeamSlotPositionField,
  TeamMemberField,
} from 'domain/shared/newProject';
import { EmployeeDisplayDataProvider, PositionDropdown, SOFTWARE_ENGINEER_ID } from 'domain/shared/employee';
import { TechnologyDropdown } from 'domain/shared/career';
import { PROJECT_MEMBERSHIP_ROLES } from 'domain/shared/core/constants';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import UpdateAssignmentAction from '../../../dashboard/shared/staffingContent/assignments/createUpdate/update/UpdateAssignmentAction';
import { SetStaffingProjectData, StaffingAssignment, StaffingProjectData } from 'domain/shared/staffing';
import CreateAssignmentAction from '../../../dashboard/shared/staffingContent/assignments/createUpdate/create/CreateAssignmentAction';
import { Alert } from 'framework/ui/mui-builder';
import { useShowContext } from 'framework/show';
import { AssignmentDateRangeField } from 'domain/shared/assignment';

interface RequiredPositionsListProps {
  projectData: StaffingProjectData;
  setProjectData?: SetStaffingProjectData;
}

const { ACCOUNT_MANAGER: _ACCOUNT_MANAGER, ...projectRolesForDropdown } = PROJECT_MEMBERSHIP_ROLES;

const hasPlannedAssignments = (slot: FormFieldsData) => {
  return (slot?.getValueBySource?.('members') || slot?.members || []).filter(Boolean).length > 0;
};

const RequiredPositionsList = ({ projectData, setProjectData }: RequiredPositionsListProps) => {
  const { data: { items = [] } = {} } = useListContext();
  const employeeAliases = Array.from(
    new Set(
      items.flatMap((slot: { members?: Array<{ employeeAlias?: string }> }) =>
        (slot?.members || []).flatMap(({ employeeAlias }) => (employeeAlias ? [employeeAlias] : [])),
      ),
    ),
  );

  const { setData: setFormShowData } = useShowContext();

  const assignmentsSignature = (projectData.assignments || [])
    .map((assignment: StaffingAssignment) => [assignment.id, assignment.slotId].join(':'))
    .join('|');

  const handleSetProjectData: SetStaffingProjectData = nextProjectData => {
    setProjectData?.(nextProjectData);
    setFormShowData?.({
      ...nextProjectData,
      team: nextProjectData.team,
    });
  };

  if (!items.length) {
    return <Alert severity="info">No required positions available</Alert>;
  }
  return (
    <EmployeeDisplayDataProvider aliases={employeeAliases}>
      <Stack>
        <EditableListDataGridLayout
          rerenderTableCellsDeps={[assignmentsSignature]}
          disabledAddNewRow
          customEnptyMessage="No required positions available"
          getRowEnabled={() => true}
          disabledRemove={data => !!data}
          newRowDefaultData={{ members: [], issues: [], assignedFte: 0 }}
          rowProps={{
            padding: 0,
            px: 0,
            py: 1,
          }}
          getTableCells={isEditMode => {
            return Promise.resolve([
              {
                type: EDITABLE_LIST_GROUP,
                component: Grid,
                componentProps: {
                  container: true,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  columnSpacing: 1,
                  columns: 12,
                  borderBottom: !isEditMode && '1px solid var(--mui-palette-TableCell-border)',
                  pb: !isEditMode ? 1 : 0,
                },
                gridCellProps: {
                  size: 12,
                },
                children: isEditMode
                  ? [
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'positionId',
                        isEditor: true,
                        component: PositionDropdown,
                        componentProps: { label: 'Position', disabled: true },
                        gridCellProps: { size: 3 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'requiredRole',
                        isEditor: true,
                        component: EnumDropdown,
                        componentProps: {
                          disabled: true,
                          options: projectRolesForDropdown,
                          label: 'Project role',
                        },
                        gridCellProps: { size: 3 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'competencyLevel',
                        isEditor: true,
                        component: EnumDropdown,
                        componentProps: {
                          options: EMPLOYEE_COMPETENCY_LEVEL,
                          label: 'Competency level',
                          disabled: true,
                        },
                        gridCellProps: { size: 1.5 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'requiredFte',
                        isEditor: true,
                        component: NumberInput,
                        componentProps: { label: 'FTE', disabled: true },
                        gridCellProps: { size: 1.5 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'description',
                        isEditor: true,
                        component: TextInput,
                        componentProps: { label: 'Note' },
                        gridCellProps: { size: 3 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'technologyId',
                        isEditor: true,
                        component: TechnologyDropdown,
                        componentProps: { label: 'Technology', disabled: true },
                        isVisible: row => row?.positionId === SOFTWARE_ENGINEER_ID,
                        gridCellProps: { size: 3 },
                      },
                    ]
                  : [
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'positionId',
                        isEditor: false,
                        component: ProjectTeamSlotPositionField,
                        gridCellProps: { size: 2.2 },
                      },
                      {
                        type: EDITABLE_LIST_CUSTOM,
                        component: CreateAssignmentAction,
                        componentProps: { projectData, setProjectData: handleSetProjectData, isPlanned: true },
                        gridCellProps: {
                          size: 0.3,
                          sx: { display: 'flex', justifyContent: 'flex-end' },
                        },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'requiredFte',
                        isEditor: false,
                        component: FTEBadge,
                        componentProps: {
                          assignedFteSource: 'assignedFte',
                        },
                        gridCellProps: { size: 2.2 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'requiredRole',
                        isEditor: false,
                        component: ProjectTeamMemberRoleField,
                        isVisible: slot => !hasPlannedAssignments(slot),
                        gridCellProps: { size: 3.3 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'description',
                        isEditor: false,
                        component: FormattedTextField,
                        componentProps: {
                          format: createTruncatedTextFormat(40),
                        },
                        gridCellProps: { size: 2 },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'issues',
                        isEditor: false,
                        component: ProjectTeamIssuesField,
                        gridCellProps: { size: 2 },
                      },
                      {
                        type: EDITABLE_LIST_GROUP,
                        component: Grid,
                        componentProps: {
                          container: true,
                        },
                        gridCellProps: {
                          size: 12,
                        },
                        children: [
                          {
                            type: EDITABLE_LIST_NESTED_LIST,
                            source: 'members',
                            component: Grid,
                            gridCellProps: { size: 12 },
                            rows: {
                              emptyListComponent: (
                                <Typography variant="body2" color="textSecondary" pl={5}>
                                  No planned assignments.
                                </Typography>
                              ),
                            },
                            componentProps: {
                              container: true,
                              alignItems: 'center',
                              direction: 'row',
                              justifyContent: 'space-between',
                              pr: 0,
                              columnSpacing: 1,
                              columns: 12,
                            },
                            children: [
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'employeeAlias',
                                isEditor: false,
                                component: TeamMemberField,
                                gridCellProps: { size: 2.5, sx: { pl: 5 } },
                              },
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'fte',
                                isEditor: false,
                                component: FTEBadge,
                                componentProps: { isMemberFter: true },
                                gridCellProps: {
                                  size: 0.7,
                                  sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                  },
                                },
                              },
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'positionId',
                                isEditor: false,
                                component: ProjectTeamMemberPositionField,
                                componentProps: {
                                  positionIdSource: 'positionId',
                                  technologyIdSource: 'technologyId',
                                  competencyLevelSource: 'competencyLevel',
                                },
                                gridCellProps: { size: 1.5 },
                              },
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'role',
                                isEditor: false,
                                component: ProjectTeamMemberRoleField,
                                gridCellProps: { size: 1.5 },
                              },
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'startDate',
                                isEditor: false,
                                component: AssignmentDateRangeField,
                                gridCellProps: { size: 1.8 },
                              },
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'noteText',
                                isEditor: false,
                                component: FormattedTextField,
                                componentProps: {
                                  format: createTruncatedTextFormat(40),
                                },
                                gridCellProps: { size: 2 },
                              },
                              {
                                type: EDITABLE_LIST_FIELD,
                                source: 'issues',
                                isEditor: false,
                                component: ProjectTeamIssuesField,
                                gridCellProps: { size: 1.5 },
                              },

                              {
                                type: EDITABLE_LIST_CUSTOM,
                                component: UpdateAssignmentAction,
                                componentProps: { projectData, setProjectData: handleSetProjectData, isVirtual: true },
                                gridCellProps: {
                                  size: 0.5,
                                  sx: { display: 'flex', justifyContent: 'flex-end', pr: 1 },
                                },
                              },
                            ],
                          },
                        ],
                      },
                    ],
              },
            ]);
          }}
        />
      </Stack>
    </EmployeeDisplayDataProvider>
  );
};
export default RequiredPositionsList;
